import { AnimatePresence, motion } from 'framer-motion';
import { Archive, ChevronDown, FileText, PlayCircle, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePesaParcours } from '../../stores/usePesaParcours';

const ResourcePanel = ({ isOpen, setIsOpen, isVisible }) => {
  const {
    currentModule, activeInvestigation, openAsset, activeAsset,
    resolveAsset, ui, currentStep, setEmbeddedMedia, setVideoScreenFocus
  } = usePesaParcours();
  const { darkMode } = useTheme();

  const [showBonus, setShowBonus] = useState(false);

  const regularFiches = currentModule?.fiches || [];
  const bonusFiches   = currentModule?.bonusFiches || [];
  const allDisplayableAssets = [...regularFiches, ...bonusFiches];
  const linkedFicheId = activeInvestigation?.associatedFicheId || currentStep?.contextualFicheId;

  const contextualAssets = allDisplayableAssets.filter(a => (a._id || a.id) === linkedFicheId);
  const libraryAssets    = regularFiches.filter(a => (a._id || a.id) !== linkedFicheId);

  // ── Asset Button ───────────────────────────────────────────────────────
  const AssetButton = ({ asset, type = 'normal' }) => {
    // FIX #4 — cohérence _id || id sur tout le panel (bonusFiches utilisait
    // a.id sans fallback, causant des keys undefined et isActive toujours false)
    const assetId = asset._id || asset.id;
    const isActive = (activeAsset?._id || activeAsset?.id) === assetId;
    const Icon = asset.icon || (asset.type === 'video' ? PlayCircle : FileText);

    const styles = {
      context: darkMode ? 'bg-blue-500/5 border-blue-400/20'   : 'bg-blue-50/50 border-blue-200',
      bonus:   darkMode ? 'bg-amber-500/5 border-amber-400/20' : 'bg-amber-50/50 border-amber-200',
      theory:  darkMode ? 'bg-purple-500/5 border-purple-400/20' : 'bg-purple-50/50 border-purple-200',
      normal:  darkMode ? 'bg-white/[0.02] border-white/5'     : 'bg-slate-50 border-slate-100',
    };

    const handleClick = () => {
      if (asset.isTheoryStep) {
        setEmbeddedMedia({ type: asset.type, src: resolveAsset(asset.file), title: asset.titre });
      } else {
        openAsset(assetId);
      }
    };

    return (
      <motion.button
        whileHover={{ x: 4 }}
        onClick={handleClick}
        className={`w-full p-2.5 rounded-xl flex items-center gap-3 border transition-all ${styles[type]}
        ${isActive ? 'ring-1 ring-blue-500/50 border-blue-500/40' : ''}`}
      >
        <div className={`shrink-0 ${isActive ? 'text-blue-500' : 'text-slate-500'}`}>
          <Icon size={14} strokeWidth={isActive ? 2.5 : 2} />
        </div>
        {isOpen && (
          <div className="flex-1 min-w-0 text-left">
            <p className={`text-[10px] font-medium truncate ${darkMode ? 'text-slate-300' : 'text-slate-700'} ${isActive ? '!text-blue-500 font-bold' : ''}`}>
              {asset.titre || asset.title || '—'}
            </p>
          </div>
        )}
      </motion.button>
    );
  };

  useEffect(() => {
    setVideoScreenFocus(true);
    return () => setVideoScreenFocus(false);
  }, []);

  return (
    <motion.aside
      animate={{ width: !isVisible ? 0 : isOpen ? 260 : 68 }}
      className={`relative flex flex-col h-full overflow-hidden rounded-3xl border transition-colors duration-500 ${
        darkMode ? 'bg-[#0d0d12]/80 backdrop-blur-2xl border-white/5' : 'bg-white/90 backdrop-blur-md border-slate-200'
      }`}
    >
      {/* Header */}
      <div className={`h-14 shrink-0 flex items-center px-4 gap-3 border-b ${darkMode ? 'border-white/5' : 'border-slate-100'}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
            darkMode ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
          }`}
          title={isOpen ? 'Réduire' : 'Développer'}
        >
          <Archive size={14} />
        </button>
        {isOpen && (
          <div className="flex flex-col">
            <span className={`text-[7px] font-mono uppercase tracking-[0.2em] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Data_Center
            </span>
            <span className={`text-[10px] font-black uppercase ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Archives
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-6">

        {/* CONTEXTUELLE */}
        {contextualAssets.length > 0 && (
          <div className="space-y-2">
            {isOpen && <label className="text-[8px] font-bold text-blue-500/70 uppercase px-1 tracking-widest">Focus_Actuel</label>}
            {contextualAssets.map(a => (
              <AssetButton key={a._id || a.id} asset={a} type="context" />
            ))}
          </div>
        )}

        {/* BIBLIOTHÈQUE */}
        <div className="space-y-2">
          {isOpen && <label className="text-[8px] font-bold text-slate-500/70 uppercase px-1 tracking-widest">Documentation</label>}
          {libraryAssets.map(a => (
            <AssetButton key={a._id || a.id} asset={a} type="normal" />
          ))}
        </div>

        {/* BONUS — collapsable */}
        {bonusFiches.length > 0 && (
          <div className={`rounded-2xl transition-all ${showBonus && darkMode ? 'bg-white/[0.02] p-2' : ''}`}>
            <button
              onClick={() => setShowBonus(!showBonus)}
              className="w-full flex items-center justify-between px-2 py-1 group"
            >
              <div className="flex items-center gap-2">
                <Sparkles size={12} className={showBonus ? 'text-amber-500' : 'text-slate-500'} />
                {isOpen && (
                  <span className={`text-[8px] font-bold uppercase tracking-widest ${showBonus ? 'text-amber-500' : 'text-slate-500'}`}>
                    Bonus_Files
                  </span>
                )}
              </div>
              {isOpen && (
                <motion.div animate={{ rotate: showBonus ? 180 : 0 }}>
                  <ChevronDown size={12} className="text-slate-500" />
                </motion.div>
              )}
            </button>

            <AnimatePresence>
              {showBonus && isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden space-y-1 mt-2"
                >
                  {bonusFiches.map(a => (
                    // FIX #4 — key cohérente avec le reste du panel
                    <AssetButton key={a._id || a.id} asset={a} type="bonus" />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      
    </motion.aside>
  );
};

export default ResourcePanel;